import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { getUser } from "@/lib/secureStorage";
import { hasPerm } from "@/lib/auth";
import { PauseCircleIcon, PlayCircleIcon } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const CFG = {
  wo: { base: "production-orders", hold: "production_orders:wo_hold", release: "production_orders:release_hold", no: "po_no", label: "Work Order" },
  cwo: { base: "child-work-orders", hold: "child_work_orders:cwo_hold", release: "child_work_orders:release_hold", no: "cwo_no", label: "Child Work Order" },
};

const errText = (e) => {
  const d = e?.response?.data?.detail;
  return (d && typeof d === "object" ? d.message || d.code : d) || "Action failed";
};

// WO-HOLD-01 · Hold / Release buttons with a mandatory reason (min 4 chars). Backend is authoritative.
export function HoldReleaseButtons({ kind, doc, onDone }) {
  const c = CFG[kind];
  const [action, setAction] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  if (!doc?.id) return null;
  const isAdmin = getUser()?.role === "Administrator";
  const held = doc.on_hold === true;
  const allowed = isAdmin || hasPerm(null, held ? c.release : c.hold);
  if (!allowed || ["Cancelled", "Completed", "Closed"].includes(doc.status) && !held) return null;
  const tid = `${kind}-${held ? "release" : "hold"}`;

  const submit = async () => {
    const r = reason.trim();
    if (r.length < 4) { toast.error("Reason is required (min 4 characters)"); return; }
    setBusy(true);
    try {
      await api.post(`/${c.base}/${doc.id}/${action === "hold" ? "hold" : "release-hold"}`, { reason: r });
      toast.success(`${doc[c.no] || c.label} · ${action === "hold" ? "placed on Hold" : "Hold released"}`);
      setAction(null);
      onDone?.();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button type="button" data-testid={`${tid}-btn`} onClick={() => { setReason(""); setAction(held ? "release" : "hold"); }}
        className={`px-4 py-2 rounded-sm border text-sm font-semibold flex items-center gap-2 ${held
          ? "border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/10"
          : "border-amber-500/50 text-amber-300 hover:bg-amber-500/10"}`}>
        {held ? <PlayCircleIcon size={14} weight="bold" /> : <PauseCircleIcon size={14} weight="bold" />}
        {held ? "Release Hold" : "Hold"}
      </button>
      <Dialog open={!!action} onOpenChange={(o) => !o && !busy && setAction(null)}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] text-white max-w-md" data-testid={`${tid}-dialog`}>
          <DialogHeader>
            <DialogTitle className="font-display">
              {action === "hold" ? "Put on Hold" : "Release Hold"} · {doc[c.no]}
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-zinc-400">
            {action === "hold"
              ? (kind === "wo" ? "All active Child WOs are held with it. Changes are blocked until released." : "Changes to this Child WO are blocked until released.")
              : (kind === "wo" ? "Child WOs held by this Work Order are released with it." : "This Child WO becomes editable again.")}
          </p>
          <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Reason *</label>
          <textarea data-testid={`${tid}-reason`} value={reason} onChange={e => setReason(e.target.value)} rows={3} autoFocus
            placeholder="Minimum 4 characters"
            className="w-full px-3 py-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)]" />
          <DialogFooter className="gap-2">
            <button type="button" onClick={() => setAction(null)} disabled={busy}
              className="px-4 py-2 rounded-sm border border-[var(--border)] text-sm text-zinc-400">Cancel</button>
            <button type="button" data-testid={`${tid}-confirm`} onClick={submit} disabled={busy || reason.trim().length < 4}
              className="px-4 py-2 rounded-sm bg-[var(--accent)] hover:bg-blue-600 disabled:bg-zinc-700 disabled:cursor-not-allowed text-sm font-semibold text-white">
              {busy ? "Saving…" : action === "hold" ? "Confirm Hold" : "Confirm Release"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function OnHoldBadge({ doc, testId }) {
  if (doc?.on_hold !== true) return null;
  return (
    <span data-testid={testId} title={doc.held_by_parent ? "Held by parent Work Order" : ""}
      className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm border bg-amber-500/10 text-amber-300 border-amber-500/40">
      On Hold{doc.held_by_parent ? " · Parent" : ""}
    </span>
  );
}
